// An agent's knowledge base (DOCS-KB): the pages, spaces and page kinds a digital employee or a
// personal crew reads. The list is base plan (the Knowledge tab shows with the `agents` add-on
// off, read-only); adding and removing sources is the add-on's. Edited by the agent's supervisor
// (the holder of its seat's supervisor seat), a crew's owner, and company admins (`can_edit`).
import { z } from "zod";
import { KnowledgeSourceKind, PageKind } from "../enums";
import { AgentKnowledgeSource } from "../knowledge-rows";
import { Timestamp, Uuid } from "../primitives";
import { PageSummary, WritablePageKind } from "./pages";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}/agents/{membership_id}/knowledge";
const core = { feature: "core" } as const;
const agents = { feature: "agents" } as const;

export const AgentKnowledgeParams = z.object({ co: Uuid, membership_id: Uuid });
export const AgentKnowledgeItemParams = z.object({
  co: Uuid,
  membership_id: Uuid,
  source_id: Uuid,
});

/** A source with what the tab shows: the page or space it names, and how many pages it covers now. */
export const KnowledgeSourceView = AgentKnowledgeSource.extend({
  page: PageSummary.extend({ space_name: z.string() }).nullable(),
  space: z.object({ id: Uuid, name: z.string(), kind: z.string() }).nullable(),
  /** Live pages the source resolves to today (0 for an archived page or an empty space). */
  page_count: z.int().min(0),
}).meta({ id: "KnowledgeSourceView" });
export type KnowledgeSourceView = z.infer<typeof KnowledgeSourceView>;

export const AgentKnowledge = z
  .object({
    agent: z.object({
      membership_id: Uuid,
      display_name: z.string(),
      kind: z.enum(["digital_employee", "personal_agent"]),
    }),
    data: z.array(KnowledgeSourceView),
    can_edit: z.boolean(),
    /** The most recent run that received the knowledge, across the sources. */
    last_read_at: Timestamp.nullable(),
  })
  .meta({ id: "AgentKnowledge" });
export type AgentKnowledge = z.infer<typeof AgentKnowledge>;

export const KnowledgeSourceInput = z
  .discriminatedUnion("kind", [
    z.strictObject({ kind: z.literal("page"), page_id: Uuid }),
    z.strictObject({ kind: z.literal("space"), space_id: Uuid }),
    z.strictObject({ kind: z.literal("kind"), page_kind: WritablePageKind }),
  ])
  .meta({ id: "KnowledgeSourceInput" });
export type KnowledgeSourceInput = z.infer<typeof KnowledgeSourceInput>;

export const KnowledgeSourcesAdd = z
  .strictObject({ sources: z.array(KnowledgeSourceInput).min(1).max(50) })
  .meta({ id: "KnowledgeSourcesAdd" });

/** Re-exported for the picker's filter chips and the readers list. */
export const KnowledgeKinds = { source: KnowledgeSourceKind, page: PageKind } as const;

export const knowledgeRoutes = {
  "agentKnowledge.list": defineRoute({
    ...core,
    id: "agentKnowledge.list",
    method: "get",
    path: base,
    tag: "knowledge",
    summary: "The docs and spaces an agent reads, with when each was last read",
    access: "member",
    params: AgentKnowledgeParams,
    response: { status: 200, schema: AgentKnowledge },
    errors: [],
    emits: [],
  }),
  "agentKnowledge.add": defineRoute({
    ...agents,
    id: "agentKnowledge.add",
    method: "post",
    path: base,
    tag: "knowledge",
    summary: "Add pages, spaces or page kinds to an agent's knowledge base",
    access: "member",
    params: AgentKnowledgeParams,
    body: KnowledgeSourcesAdd,
    idempotency: "optional",
    response: { status: 201, schema: AgentKnowledge },
    errors: [],
    emits: ["agent.knowledge_changed"],
  }),
  "agentKnowledge.remove": defineRoute({
    ...agents,
    id: "agentKnowledge.remove",
    method: "delete",
    path: `${base}/{source_id}`,
    tag: "knowledge",
    summary: "Remove a source from an agent's knowledge base",
    access: "member",
    params: AgentKnowledgeItemParams,
    response: { status: 204 },
    errors: [],
    emits: ["agent.knowledge_changed"],
  }),
} as const;
